/**
 * MP PDP Guarantees (PDP)
 * ADDED: 2026-07-31 by MeshPlanet
 *
 * <mp-pdp-guarantees> is placed inside the add to cart group in the theme
 * editor, but the design puts the row directly on top of the add to cart
 * button — and the Stylux personalization card lives *inside* the button's
 * container (see snippets/add-to-cart-button.liquid), which no block position
 * can reach from the group. So the element moves itself next to the button:
 *
 *   add-to-cart-component
 *     ├─ mp-stylux-offer      (hidden until the SDK injects its offer button)
 *     ├─ mp-pdp-guarantees    <- moved here
 *     └─ button[ref=addToCartButton]
 *
 * That single rule covers both states the design asks for: with the Stylux
 * widget the row sits between the card and the button, and without it the row
 * sits straight on top of the button.
 *
 * Variant changes only morph the button itself (assets/product-form.js), so the
 * move survives them. A section re-render puts a fresh element back in the
 * group slot, which moves itself again on connect.
 *
 * Data attributes:
 *   data-attach-to-atc  'true' to move the row above the add to cart button
 */
(function () {
  'use strict';

  var PRODUCT_SCOPE = '[id*="ProductInformation-"]';

  /* Most specific first: the main buy buttons, then any add to cart component
     in the same section, so a theme with a custom button still gets the row. */
  var BUTTON_SELECTORS = [
    '.buy-buttons-block add-to-cart-component [ref="addToCartButton"]',
    'add-to-cart-component [ref="addToCartButton"]',
    '[ref="addToCartButton"]',
  ];

  class MpPdpGuarantees extends HTMLElement {
    connectedCallback() {
      if (this.dataset.attachToAtc !== 'true') return;

      /* The block renders before the buy buttons, and in the theme editor it can
         be inserted before them as well, so a missing button is expected and
         only means "not yet". */
      if (this.place()) return;

      this.watch();
    }

    disconnectedCallback() {
      this.unwatch();
    }

    /** @returns {HTMLElement | null} */
    get scope() {
      return /** @type {HTMLElement | null} */ (this.closest(PRODUCT_SCOPE));
    }

    /** @returns {HTMLElement | null} */
    findButton() {
      var scope = this.scope || document;

      for (var index = 0; index < BUTTON_SELECTORS.length; index++) {
        var button = /** @type {HTMLElement | null} */ (scope.querySelector(BUTTON_SELECTORS[index]));

        if (button) return button;
      }

      return null;
    }

    /**
     * @returns {boolean} Whether the row ended up above the add to cart button.
     */
    place() {
      var button = this.findButton();
      var target = button && button.parentElement;

      if (!button || !target) return false;

      /* insertBefore below re-runs connectedCallback: without this the element
         would keep re-placing itself. */
      if (this.nextElementSibling === button) {
        this.dataset.mpAttached = 'true';
        return true;
      }

      target.insertBefore(this, button);
      this.dataset.mpAttached = 'true';

      return true;
    }

    watch() {
      if (this.observer) return;

      var root = this.scope;

      if (!root) return;

      this.observer = new MutationObserver(() => {
        if (this.place()) this.unwatch();
      });

      this.observer.observe(root, { childList: true, subtree: true });
    }

    unwatch() {
      if (!this.observer) return;

      this.observer.disconnect();
      this.observer = null;
    }
  }

  if (!customElements.get('mp-pdp-guarantees')) {
    customElements.define('mp-pdp-guarantees', MpPdpGuarantees);
  }
})();
